import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Plus, Search } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import CustomMultiSelect from '../../Components/CustomMultiSelect';
import CustomDropdown from '../../Components/Dropdown';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useTenants } from '../../hooks/tenants';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useLeaseBuildingObjects, useLeaseStairwells, useLeasesAdmin } from '../../hooks/leases';
import { Lease } from '../../types/leases';
import { LeaseObjectType } from '../../types/Enums';
import { translateLeaseObjectType } from '../../types/EnumTranslations';
import { leaseStatus, todayStockholm } from '../../Helpers/leaseUtils';
import { uploadLeaseContract } from '../../Helpers/leaseContractUtils';
import LeaseCard from './LeaseCard';
import LeaseModal, { LeaseModalSubmitData } from './LeaseModal';
import LeasesTable from './LeasesTable';
import LeasesSummary from './LeasesSummary';
import LeaseQueueCloseModal from '../ParkingQueue/LeaseQueueCloseModal';
import {
    DEFAULT_LEASE_SORT,
    LeaseNames,
    LeaseSort,
    OBJECT_TYPE_FILTERS,
    DEFAULT_RENT_PERIOD,
    RENT_PERIODS,
    RentPeriod,
    buildingKeysFromParams,
    filterLeases,
    leaseSummary,
    nextSort,
    sortLeases,
} from './leasesView';
import './Leases.css';

/**
 * Hyresavtal: every lease across buildings (/leases), under Hyresadmin.
 * `?building=` (repeatable) is the Byggnad filter, `?lease=` opens one lease;
 * search, object type, Dölj avslutade and the sort live for the session. Ended leases are
 * shown by default, their rent struck through.
 */
const LeasesPage: React.FC = () => {
    useDocumentTitle('Hyresavtal');
    const tenantWord = useTenantTerm().singular.toLowerCase();
    const [searchParams, setSearchParams] = useSearchParams();
    const buildingKeys = useMemo(() => buildingKeysFromParams(searchParams), [searchParams]);
    const openLeaseKey = searchParams.get('lease');
    const isAdmin = useIsAdmin();
    const { getTenantName } = useTenants();

    const [search, setSearch] = useState('');
    const [objectTypes, setObjectTypes] = useState<LeaseObjectType[]>([]);
    const [rentPeriod, setRentPeriod] = useState<RentPeriod>(DEFAULT_RENT_PERIOD);
    const [hideEnded, setHideEnded] = useState(false);
    const [sort, setSort] = useState<LeaseSort>(DEFAULT_LEASE_SORT);
    const [uploadWarning, setUploadWarning] = useState<string | null>(null);

    // Building pages keep the last building in memory and refetch only on a key change, so a
    // lease change here (the callable moves currentLeaseKey) reloads that building.
    const { currentBuilding, fetchBuilding } = useBuilding();
    const cachedBuildingKey = currentBuilding?.key;
    const reloadCachedBuilding = useCallback(async () => {
        if (cachedBuildingKey) await fetchBuilding(cachedBuildingKey);
    }, [cachedBuildingKey, fetchBuilding]);

    const {
        leases, loading, loadError, buildings, buildingName,
        submitting, error, clearError, createLease, updateLease, endLease, deleteLease, addContract, removeContract, renameContract,
    } = useLeasesAdmin({ onMutated: reloadCachedBuilding });

    const { objectLabel } = useLeaseStairwells(leases);
    const names: LeaseNames = useMemo(() => ({ buildingName, tenantName: getTenantName, objectLabel }), [buildingName, getTenantName, objectLabel]);

    // Modals: create/edit, end, and the lease detail (driven by ?lease=).
    const [showModal, setShowModal] = useState(false);
    const [editingLease, setEditingLease] = useState<Lease | undefined>();
    const [newLeaseBuildingKey, setNewLeaseBuildingKey] = useState('');
    // The lease just written, while asking whether it fills one of the tenant's köplatser (parking leases only).
    const [queueCheckLease, setQueueCheckLease] = useState<Lease | null>(null);
    const [endingLease, setEndingLease] = useState<Lease | undefined>();
    const [endDate, setEndDate] = useState(todayStockholm());
    const modalBuildingKey = editingLease?.buildingKey ?? newLeaseBuildingKey;
    const { objects: modalObjects, loading: modalObjectsLoading, error: modalObjectsError } =
        useLeaseBuildingObjects(showModal ? modalBuildingKey || undefined : undefined);

    const updateParams = useCallback((change: (params: URLSearchParams) => void) => {
        setSearchParams(current => {
            const next = new URLSearchParams(current);
            change(next);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const setBuildingFilter = (keys: string[]) => updateParams(params => {
        params.delete('building');
        // Every building chosen is the same as none: keep the URL short.
        if (keys.length < buildings.length) keys.forEach(key => params.append('building', key));
    });
    const openLease = useCallback((leaseKey: string) => updateParams(params => params.set('lease', leaseKey)), [updateParams]);
    const closeLease = useCallback(() => updateParams(params => params.delete('lease')), [updateParams]);

    const openedLease = openLeaseKey ? leases.find(l => l.key === openLeaseKey) : undefined;
    // A link to an ended lease shows the ended ones too, so the row is in the table behind the modal.
    useEffect(() => {
        if (openedLease && leaseStatus(openedLease) === 'ended') setHideEnded(false);
    }, [openedLease]);

    // Every lease matching the filters, ended included: the tiles and the ended count use these.
    const matchingLeases = useMemo(
        () => filterLeases(leases, { buildingKeys, search, objectTypes, showEnded: true }, names),
        [leases, buildingKeys, search, objectTypes, names]
    );
    const summary = useMemo(() => leaseSummary(matchingLeases), [matchingLeases]);
    const endedCount = matchingLeases.filter(l => leaseStatus(l) === 'ended').length;
    const visibleLeases = useMemo(
        () => sortLeases(hideEnded ? matchingLeases.filter(l => leaseStatus(l) !== 'ended') : matchingLeases, sort, names),
        [matchingLeases, hideEnded, sort, names]
    );

    const singleBuildingKey = buildingKeys.length === 1 ? buildingKeys[0] : undefined;

    const startNewLease = () => {
        setEditingLease(undefined);
        setNewLeaseBuildingKey(singleBuildingKey ?? '');
        setShowModal(true);
    };

    const uploadContracts = async (leaseKey: string, filesToUpload: File[]) => {
        const user = getAuth().currentUser;
        if (!user) return;
        for (const file of filesToUpload) {
            const result = await uploadLeaseContract(leaseKey, file, user.uid);
            if (result.success && result.attachment) {
                await addContract(leaseKey, result.attachment);
            } else if (result.error) {
                throw new Error(result.error);
            }
        }
    };

    const closeLeaseModal = () => {
        setShowModal(false);
        setEditingLease(undefined);
    };

    const handleSubmit = async ({ input, filesToUpload, contractsToRemove }: LeaseModalSubmitData) => {
        setUploadWarning(null);
        if (!editingLease) {
            const created = await createLease(input);
            try {
                await uploadContracts(created.key, filesToUpload);
            } catch (err) {
                // The lease exists: leaving the form in create mode would invite a second lease on retry.
                // Close it and say how to add the file; the hook's own error would repeat the cause.
                closeLeaseModal();
                clearError();
                setUploadWarning(`Hyresavtalet skapades, men en avtalsfil kunde inte laddas upp (${err instanceof Error ? err.message : 'okänt fel'}). Öppna avtalet och lägg till filen under Redigera.`);
                setQueueCheckLease(created);
                return;
            }
            closeLeaseModal();
            setQueueCheckLease(created);
            return;
        }
        await updateLease(editingLease.key, input);
        await uploadContracts(editingLease.key, filesToUpload);
        for (const attachmentKey of contractsToRemove) {
            await removeContract(editingLease.key, attachmentKey);
        }
        closeLeaseModal();
    };

    const handleEnd = async () => {
        if (!endingLease) return;
        try {
            await endLease(endingLease.key, endDate);
            setEndingLease(undefined);
        } catch {
            // error is shown by the hook
        }
    };

    if (loading && leases.length === 0) return <PageLoader />;

    const buildingOptions = buildings.map(b => ({ label: b.name || 'Namnlös byggnad', value: b.key }));
    const typeOptions = OBJECT_TYPE_FILTERS.map(type => ({ label: translateLeaseObjectType(type), value: type }));
    const filtered = buildingKeys.length > 0 || search.trim() !== '' || objectTypes.length > 0;

    return (
        <div className="leases-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={singleBuildingKey
                        ? [{ text: buildingName(singleBuildingKey), link: `/building/${singleBuildingKey}` }, { text: 'Hyresavtal' }]
                        : [{ text: 'Hyresavtal' }]}
                />
            </div>

            <div className="leases-content leases-content--wide">
                <div className="leases-header">
                    <div>
                        <h1 className="page-title">Hyresavtal</h1>
                        <LeasesSummary
                            summary={summary}
                            tenantName={getTenantName}
                            buildingName={singleBuildingKey ? undefined : buildingName}
                            onOpenLease={openLease}
                            objectLabel={objectLabel}
                        />
                    </div>
                </div>

                <div className="leases-filters">
                    <CustomMultiSelect
                        label="Byggnad"
                        options={buildingOptions}
                        value={buildingKeys}
                        onChange={value => setBuildingFilter(value as string[])}
                        placeholder="Alla byggnader"
                        selectedItemsLabel="{0} byggnader"
                    />
                    <CustomMultiSelect
                        label="Typ"
                        options={typeOptions}
                        value={objectTypes}
                        onChange={value => setObjectTypes(value as LeaseObjectType[])}
                        placeholder="Alla typer"
                        selectedItemsLabel="{0} typer"
                    />
                    <CustomDropdown<RentPeriod>
                        label="Visa hyra"
                        options={RENT_PERIODS.map(({ key, name }) => ({ key, name }))}
                        selectedKey={rentPeriod}
                        onSelect={setRentPeriod}
                        showAllOption={false}
                    />
                    <div className="leases-search">
                        <Search size={16} className="leases-search-icon" aria-hidden="true" />
                        <Form.Control
                            type="search"
                            placeholder={`Sök ${tenantWord} eller objekt`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            aria-label={`Sök ${tenantWord} eller objekt`}
                        />
                    </div>
                    {endedCount > 0 && (
                        <Form.Check
                            type="switch"
                            id="hide-ended-leases"
                            label={`Dölj avslutade (${endedCount})`}
                            checked={hideEnded}
                            onChange={e => setHideEnded(e.target.checked)}
                        />
                    )}
                    {isAdmin && (
                        <Button variant="primary" className="leases-new-button" onClick={startNewLease}>
                            <Plus size={18} className="me-1" />Nytt avtal
                        </Button>
                    )}
                </div>

                {loadError && <Alert variant="danger">{loadError}</Alert>}
                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}
                {uploadWarning && <Alert variant="warning" dismissible onClose={() => setUploadWarning(null)}>{uploadWarning}</Alert>}

                {visibleLeases.length === 0 ? (
                    <p className="text-muted">
                        {leases.length === 0
                            ? 'Inga hyresavtal registrerade.'
                            : hideEnded && endedCount > 0
                                ? 'Inga pågående avtal. Slå av "Dölj avslutade" för historiken.'
                                : filtered ? 'Inga hyresavtal matchar filtret.' : 'Inga pågående avtal.'}
                    </p>
                ) : (
                    <LeasesTable
                        leases={visibleLeases}
                        names={names}
                        sort={sort}
                        onSort={key => setSort(current => nextSort(current, key))}
                        showBuilding={!singleBuildingKey}
                        onOpen={lease => openLease(lease.key)}
                        rentPeriod={rentPeriod}
                    />
                )}
            </div>

            <Modal show={!!openedLease} onHide={closeLease} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Hyresavtal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {openedLease && (
                        <LeaseCard
                            lease={openedLease}
                            getTenantName={getTenantName}
                            buildingName={buildingName(openedLease.buildingKey)}
                            objectLabel={objectLabel(openedLease)}
                            buildingLink={`/building/${openedLease.buildingKey}`}
                            isAdmin={isAdmin}
                            submitting={submitting}
                            onEdit={() => { closeLease(); setEditingLease(openedLease); setShowModal(true); }}
                            onEnd={() => { closeLease(); setEndDate(todayStockholm()); setEndingLease(openedLease); }}
                            onDelete={() => deleteLease(openedLease.key).then(closeLease).catch(() => undefined)}
                            onRenameContract={(attachmentKey, displayName) => renameContract(openedLease.key, attachmentKey, displayName)}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {showModal && (
                <LeaseModal
                    show={showModal}
                    onHide={closeLeaseModal}
                    onSubmit={handleSubmit}
                    buildingKey={modalBuildingKey}
                    areas={modalObjects.areas}
                    units={modalObjects.storageUnits}
                    levels={modalObjects.levels}
                    rentalUnits={modalObjects.rentalUnits}
                    buildingLeases={leases.filter(l => l.buildingKey === modalBuildingKey)}
                    lease={editingLease}
                    submitting={submitting}
                    buildingChoice={{
                        buildings: buildings.map(b => ({ key: b.key, name: b.name || 'Namnlös byggnad' })),
                        onChange: setNewLeaseBuildingKey,
                        loading: modalObjectsLoading,
                        error: modalObjectsError,
                    }}
                />
            )}
            <LeaseQueueCloseModal lease={queueCheckLease} onDone={() => setQueueCheckLease(null)} />

            <Modal show={!!endingLease} onHide={() => setEndingLease(undefined)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Avsluta hyresavtal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Avtalet gäller till och med slutdatumet. Hyresobjektet blir ledigt dagen efter.</p>
                    <Form.Group>
                        <Form.Label>Slutdatum</Form.Label>
                        <Form.Control type="date" value={endDate} min={endingLease?.startDate} onChange={e => setEndDate(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEndingLease(undefined)} disabled={submitting}>Avbryt</Button>
                    <Button variant="primary" onClick={handleEnd} disabled={submitting || !endDate}>Avsluta</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeasesPage;
